import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container } from '../../ui';
import styles from './TechnicalIntegration.module.css';

const plugins = [
  { src: '/images/logos/prestashop.png', alt: 'PrestaShop' },
  { src: '/images/logos/shopify.png', alt: 'Shopify' },
  { src: '/images/logos/woocommerce.jpg', alt: 'WooCommerce' },
  { alt: 'API REST', icon: true },
];

export function TechnicalIntegration() {
  const { t } = useTranslation('catalogue');
  const [activeTab, setActiveTab] = useState<'api' | 'plugins'>('api');

  const apiFeatures = t('integration.apiFeatures', { returnObjects: true }) as string[];

  return (
    <Section variant="light">
      <Container>
        <div className={styles.integrationHeader}>
          <span className={styles.badge}>{t('integration.badge')}</span>
          <h2 className={styles.integrationTitle}>{t('integration.title')}</h2>
          <p className={styles.integrationDesc}>{t('integration.desc')}</p>
        </div>

        <div className={styles.terminal}>
          <div className={styles.terminalBar}>
            <div className={styles.terminalDots}>
              <span className={styles.dotRed} />
              <span className={styles.dotYellow} />
              <span className={styles.dotGreen} />
            </div>
            <div className={styles.terminalLabel}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 19h8" />
                <path d="m4 17 6-6-6-6" />
              </svg>
              <span>{t('integration.terminalLabel')}</span>
            </div>
            <div className={styles.terminalStatus}>
              <span className={styles.statusDot} />
              <span className={styles.statusText}>{t('integration.terminalStatus')}</span>
            </div>
          </div>
          <div className={styles.terminalTab}>
            <span>{t('integration.terminalTab')}</span>
          </div>
          <div className={styles.terminalBody}>
            <div className={styles.terminalRequest}>
              <div className={styles.codeComment}>{'// REQUEST'}</div>
              <div>
                <span className={styles.codeGreen}>GET</span>
                <span className={styles.codeWhite}> /retailer/catalog</span>
              </div>
              <div className={styles.codeBlock}>
                <div className={styles.codeComment}>Headers:</div>
                <div>
                  <span className={styles.codeBlue}>apikey: </span>
                  <span className={styles.codeGreen}>••••••••••••</span>
                </div>
              </div>
              <div className={styles.codeBlock}>
                <div className={styles.codeComment}>Options:</div>
                <div>
                  <span className={styles.codeBlue}>brand</span>
                  <span className={styles.codeWhite}>=</span>
                  <span className={styles.codeGreen}>Apple</span>
                  <span className={styles.codeComment}> {t('integration.filtersAvailable')}</span>
                </div>
              </div>
            </div>
            <div className={styles.terminalResponse}>
              <div className={styles.codeComment}>{'// RESPONSE'}</div>
              <div className={styles.responseStatus}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span className={styles.codeGreen}>200 OK</span>
                <span className={styles.codeComment}>• 38ms</span>
              </div>
              <pre className={styles.codeResponse}>{`{
  "code": 200,
  "response": [{
    "modelId": "68074f76...",
    "brand": "Apple",
    "model": "iPhone 13 Pro",
    "storage": "128GB"
  }]
}`}</pre>
            </div>
          </div>
        </div>

        <div className={styles.tabs}>
          <div className={styles.tabList}>
            <button
              className={`${styles.tab} ${activeTab === 'api' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('api')}
            >
              {t('integration.tabApi')}
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'plugins' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('plugins')}
            >
              {t('integration.tabPlugins')}
            </button>
          </div>

          {activeTab === 'api' && (
            <div className={styles.tabContent}>
              <div className={styles.apiCard}>
                <div className={styles.apiCardHeader}>
                  <h3 className={styles.apiCardTitle}>{t('integration.apiTitle')}</h3>
                  <p className={styles.apiCardDesc}>{t('integration.apiDesc')}</p>
                </div>
                <div className={styles.apiCardContent}>
                  <div className={styles.apiFeatures}>
                    {apiFeatures.map((feature, i) => (
                      <div key={i} className={styles.apiFeature}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.checkIcon} aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'plugins' && (
            <div className={styles.tabContent}>
              <div className={styles.apiCard}>
                <div className={styles.apiCardHeader}>
                  <h3 className={styles.apiCardTitle}>{t('integration.pluginsTitle')}</h3>
                  <p className={styles.apiCardDesc}>{t('integration.pluginsDesc')}</p>
                </div>
                <div className={styles.apiCardContent}>
                  <div className={styles.pluginGrid}>
                    {plugins.map((plugin) => (
                      <div key={plugin.alt} className={styles.pluginCard}>
                        {'src' in plugin && plugin.src ? (
                          <img src={plugin.src} alt={plugin.alt} width={56} height={56} className={styles.pluginLogo} />
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.pluginIcon} aria-hidden="true">
                            <polyline points="16 18 22 12 16 6" />
                            <polyline points="8 6 2 12 8 18" />
                          </svg>
                        )}
                        <span className={styles.pluginName}>{plugin.alt}</span>
                        <span className={styles.pluginBadge}>{t('integration.pluginAvailable')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </Section>
  );
}
